import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Send } from "lucide-react";

import { ContractIntelligenceDiffCard } from "@/components/contract-intelligence/ContractIntelligenceDiffCard";
import { Badge, Button, ScrollArea, Textarea } from "@/components/ui";
import { highlightContractText } from "@/lib/contractIntelligence/highlightContract";
import { CONTRACT_HIGHLIGHTS, inferEditDiff, mockAiReply } from "@/lib/contractIntelligence/mockEdits";
import {
  EDITOR_STARTER_MESSAGE,
  SUGGESTED_EDITS,
} from "@/mockData/contractIntelligence";
import type {
  ContractIntelligenceContract,
  ContractIntelligenceEditDiff,
  ContractIntelligenceMessage,
} from "@/types/contractIntelligence.types";

interface ContractIntelligenceEditorProps {
  contract: ContractIntelligenceContract;
  onBackToDash: () => void;
}

export function ContractIntelligenceEditor({ contract, onBackToDash }: ContractIntelligenceEditorProps) {
  const [messages, setMessages] = useState<ContractIntelligenceMessage[]>([
    { role: "ai", text: EDITOR_STARTER_MESSAGE },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [contractText, setContractText] = useState(contract.contractText);
  const [appliedEdits, setAppliedEdits] = useState<ContractIntelligenceEditDiff[]>([]);
  const [showHighlights, setShowHighlights] = useState(true);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const acceptDiff = useCallback((diff: ContractIntelligenceEditDiff, messageIndex: number) => {
    setContractText((text) => text.replace(diff.before, diff.after));
    setAppliedEdits((prev) => [...prev, diff]);
    setMessages((prev) =>
      prev.map((msg, i) =>
        i === messageIndex
          ? {
              ...msg,
              diff: undefined,
              accepted: true,
              text: `${msg.text}\n\n✓ Edit applied to ${diff.section}`,
            }
          : msg,
      ),
    );

    window.setTimeout(() => {
      if (!docRef.current) return;
      const target = Array.from(docRef.current.querySelectorAll("p")).find((p) =>
        p.textContent?.includes(diff.after.slice(0, 60)),
      );
      if (target) {
        target.classList.add("bg-success/10");
        window.setTimeout(() => target.classList.remove("bg-success/10"), 1800);
      }
    }, 80);
  }, []);

  const send = useCallback(
    (text?: string) => {
      const prompt = (text ?? input).trim();
      if (!prompt || loading) return;

      setInput("");
      setMessages((prev) => [...prev, { role: "user", text: prompt }, { role: "ai", text: "", loading: true }]);
      setLoading(true);

      const diff = inferEditDiff(prompt);
      const reply = mockAiReply(prompt, Boolean(diff));

      window.setTimeout(() => {
        setMessages((prev) => {
          const withoutLoading = prev.slice(0, -1);
          const aiMessage: ContractIntelligenceMessage = { role: "ai", text: reply, diff: diff ?? undefined };
          return [...withoutLoading, aiMessage];
        });
        setLoading(false);
      }, 650);
    },
    [input, loading],
  );

  const savedTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const paragraphs = contractText.split("\n\n");

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-3 border-b border-border px-5 py-3">
        <Button type="button" variant="outline" size="sm" onClick={onBackToDash}>
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Dashboard
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="truncate text-[13px] font-semibold">{contract.name}</h1>
            <Badge variant="outline" style={{ color: contract.typeColor, borderColor: `${contract.typeColor}55` }}>
              {contract.type}
            </Badge>
            {appliedEdits.length > 0 ? (
              <Badge variant="outline" className="border-success/30 text-success">
                {appliedEdits.length} edit{appliedEdits.length > 1 ? "s" : ""} applied
              </Badge>
            ) : null}
          </div>
          <p className="text-[11px] text-muted-foreground">Working draft · auto-saved {savedTime}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button
            type="button"
            variant={showHighlights ? "default" : "outline"}
            size="sm"
            className="h-8 text-xs"
            onClick={() => setShowHighlights((v) => !v)}
          >
            Highlights
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 text-xs">
            Export
          </Button>
          <Button type="button" size="sm" className="h-8 text-xs">
            Send to counsel
          </Button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <section className="flex min-w-0 flex-1 flex-col overflow-hidden border-r border-border">
          <div className="flex shrink-0 items-center gap-1 border-b border-border bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
            <span className="rounded px-2 py-0.5 font-semibold">Read</span>
            <span className="text-muted-foreground/60">·</span>
            <span>{contract.pages} pages</span>
          </div>
          <ScrollArea className="min-h-0 flex-1">
            <div ref={docRef} className="mx-auto max-w-2xl space-y-3 px-8 py-8 font-serif text-sm leading-relaxed">
              {paragraphs.map((para) => (
                <p key={para.slice(0, 40)}>
                  {showHighlights ? highlightContractText(para, CONTRACT_HIGHLIGHTS) : para}
                </p>
              ))}
            </div>
          </ScrollArea>
        </section>

        <aside className="flex w-[min(100%,380px)] shrink-0 flex-col overflow-hidden bg-card">
          <ScrollArea className="min-h-0 flex-1 px-3 py-3">
            <div className="flex flex-col gap-3">
              {messages.map((msg, index) =>
                msg.role === "user" ? (
                  <div
                    key={`user-${index}`}
                    className="ml-6 self-end rounded-xl rounded-br-sm bg-primary px-3 py-2 text-[13px] leading-relaxed text-primary-foreground"
                  >
                    {msg.text}
                  </div>
                ) : (
                  <div key={`ai-${index}`} className="flex gap-2">
                    <span
                      className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-primary text-[11px] font-bold text-white"
                      aria-hidden="true"
                    >
                      A
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="rounded-xl rounded-bl-sm border border-border bg-muted/30 px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap">
                        {msg.loading ? (
                          <span className="inline-flex gap-1" aria-label="Ari is typing">
                            <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground" />
                            <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground [animation-delay:0.2s]" />
                            <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground [animation-delay:0.4s]" />
                          </span>
                        ) : (
                          msg.text
                        )}
                      </div>
                      {msg.diff ? (
                        <ContractIntelligenceDiffCard
                          diff={msg.diff}
                          onAccept={() => acceptDiff(msg.diff!, index)}
                          onReject={() =>
                            setMessages((prev) =>
                              prev.map((m, i) => (i === index ? { ...m, diff: undefined } : m)),
                            )
                          }
                        />
                      ) : null}
                    </div>
                  </div>
                ),
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>

          <div className="shrink-0 border-t border-border p-3">
            <div className="mb-2 flex gap-1 overflow-x-auto pb-1">
              {SUGGESTED_EDITS.map((edit) => (
                <button
                  key={edit.label}
                  type="button"
                  onClick={() => send(edit.prompt)}
                  className="shrink-0 rounded-full border border-border bg-muted/30 px-2.5 py-1 text-[10.5px] whitespace-nowrap transition-colors hover:bg-muted/60"
                >
                  {edit.icon} {edit.label}
                </button>
              ))}
            </div>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask Ari to redline a clause…"
                rows={2}
                className="min-h-[52px] resize-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <Button type="submit" size="icon" className="shrink-0 self-end" disabled={loading || !input.trim()} aria-label="Send message">
                <Send className="size-4" />
              </Button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}
